import { ApiResponse, Permission, Routes } from 'app/shared/models';
import { catchError, Observable, of, shareReplay, tap } from 'rxjs';

import { Injectable } from '@angular/core';
import { SnackbarType } from '@fuse/services/snackbar';
import { translate } from '@jsverse/transloco';

import { BaseService } from '../base';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService extends BaseService {
  private readonly _endpoint = 'v4/permissions';
  private readonly _routeRequests = new Map<
    Routes,
    Observable<ApiResponse<Permission | null>>
  >();
  private _permissionToken: string | null = null;

  constructor() {
    super();
  }

  getByRoute(route: Routes) {
    const currentToken = localStorage.getItem('passport');

    if (currentToken !== this._permissionToken) {
      this._routeRequests.clear();
      this._permissionToken = currentToken;
    }

    const cachedRequest = this._routeRequests.get(route);

    if (cachedRequest) {
      return cachedRequest;
    }

    const request = this.clientService
      .get<ApiResponse<Permission | null>>(
        `/v4/permissions/route/${encodeURIComponent(route)}`
      )
      .pipe(
        catchError(({ success, error }) =>
          of(new ApiResponse<Permission | null>(success, null, error))
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this._routeRequests.set(route, request);

    return request;
  }

  getTargetUserPermissions(userId: string) {
    return this.clientService
      .get<ApiResponse<Permission[]>>(`/${this._endpoint}/users/${userId}`)
      .pipe(
        tap(({ success, data }) => {
          if (success && data) {
            return data;
          }
        }),
        catchError(({ success, error }) => {
          this.showMessage(
            SnackbarType.Error,
            translate('messages.get_all_error')
          );

          return of(new ApiResponse<Permission[]>(success, [], error));
        })
      );
  }

  updateTargetUserPermissions(userId: string, permissions: Permission[]) {
    return this.clientService
      .put<ApiResponse<Permission[]>>(
        `/${this._endpoint}/users/${userId}`,
        permissions
      )
      .pipe(
        tap(({ success }) => {
          if (success) {
            this.clearRouteCache();
            this.getTargetUserPermissions(userId).subscribe();
          }
        }),
        catchError(({ success, error }) => {
          this.showMessage(
            SnackbarType.Error,
            translate('messages.get_all_error')
          );

          return of(new ApiResponse<Permission[]>(success, [], error));
        })
      );
  }

  clearRouteCache(): void {
    this._routeRequests.clear();
    this._permissionToken = null;
  }
}
